import React, { useState } from "react";
import { Wrench, MapPin, Play, ExternalLink, Settings, Activity, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const rigs = [{
  id: "rig-01",
  name: "TRD Pro Tundra 'The Beast'",
  type: "Primary Tow Vehicle",
  coordinates: "44.5902° N, 104.7146° W",
  description: "A heavy-duty overland build designed for towing capability and off-grid reliability. Modified for extended range and recovery operations in the American West.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562326/img/truck_hero_overland.png",
  videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ?si=AdJc41_l6w2_5kQ_",
  specs: [{
    component: "Suspension System",
    brand: "Icon Vehicle Dynamics",
    type: "Stage 7 Billet",
    link: "/OutAndBackPage"
  }, {
    component: "Front Bumper",
    brand: "CBI Offroad",
    type: "Aluminum Baja Series",
    link: "/OutAndBackPage"
  }, {
    component: "Winch",
    brand: "Warn",
    type: "Zeon 12-S Platinum",
    link: "/OutAndBackPage"
  }, {
    component: "Tires",
    brand: "BFGoodrich",
    type: "KO2 All-Terrain 35\"",
    link: "/OutAndBackPage"
  }, {
    component: "Roof Rack",
    brand: "Prinsu",
    type: "Cab Rack",
    link: "/OutAndBackPage"
  }, {
    component: "Lighting",
    brand: "Baja Designs",
    type: "LP9 Pro Series",
    link: "/OutAndBackPage"
  }]
}, {
  id: "rig-02",
  name: "Patriot X1 'Basecamp'",
  type: "Expedition Trailer",
  coordinates: "38.5733° N, 109.5498° W",
  description: "Compact, rugged, and fully equipped. This trailer serves as our mobile command center and family living quarters during multi-week expeditions.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562326/img/camper_trailer_setup.png",
  videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ?si=AdJc41_l6w2_5kQ_",
  specs: [{
    component: "Power System",
    brand: "Redarc",
    type: "Manager30 BMS",
    link: "/OutAndBackPage"
  }, {
    component: "Kitchen",
    brand: "Custom",
    type: "Slide-out Galley",
    link: "/OutAndBackPage"
  }, {
    component: "Tent",
    brand: "iKamper",
    type: "Skycamp 3.0",
    link: "/OutAndBackPage"
  }, {
    component: "Water Storage",
    brand: "Front Runner",
    type: "40 Gallon Tank",
    link: "/OutAndBackPage"
  }, {
    component: "Solar",
    brand: "Zamp Solar",
    type: "Obsidian 100W Panels",
    link: "/OutAndBackPage"
  }, {
    component: "Hitch",
    brand: "McHitch",
    type: "Articulating Coupling",
    link: "/OutAndBackPage"
  }]
}];
export default function TechnicalBuildGallery() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="technical-build-gallery" className="dark bg-background text-foreground py-16 md:py-24 relative overflow-hidden" ref={ref}>
      {/* Topographic Pattern Overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562326/img/topo_pattern_overlay.png')] bg-repeat bg-center mix-blend-overlay"></div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        {/* Section Header */}
        <div className={`mb-16 max-w-3xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-2 text-primary font-mono text-sm tracking-widest mb-4 uppercase">
            <Wrench className="w-4 h-4" />
            <span>Workshop Manual</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-sans font-bold tracking-tight mb-6 text-foreground">
            Technical Build Gallery
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Detailed specifications and modification logs for our expedition vehicles. 
            Every component is tested in the field and chosen for reliability in the American West.
          </p>
        </div>

        {/* Rigs Gallery */}
        <div className="space-y-24">
          {rigs.map((rig, index) => <div key={rig.id} data-index={index} className={`group transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              {/* Rig Header */}
              <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4 border-l-2 border-primary pl-6">
                <div>
                  <h3 className="text-3xl font-serif tracking-wide text-foreground uppercase">
                    {rig.name}
                  </h3>
                  <p className="text-primary font-mono text-sm mt-1 tracking-wider uppercase">
                    {rig.type}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground font-mono text-xs">
                  <MapPin className="w-3 h-3" />
                  <span>LAST KNOWN COORD: {rig.coordinates}</span>
                </div>
              </div>

              {/* Hero Image */}
              <div className="relative w-full mb-12 rounded-sm overflow-hidden border border-border/50 shadow-2xl bg-card">
                <div className="absolute top-4 right-4 z-20 bg-background/90 backdrop-blur-sm border border-border px-3 py-1 text-xs font-mono text-foreground">
                  IMG_SEQ_{index + 1}0{index + 1}
                </div>
                <AspectRatio ratio={21 / 9} className="bg-muted">
                  <img src={rig.image} alt={rig.name} className="object-cover w-full h-full opacity-90 hover:opacity-100 transition-opacity duration-500" />
                </AspectRatio>
                {/* Scan Line Effect */}
                <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(to_right,transparent_0%,transparent_49%,rgba(255,255,255,0.05)_50%,transparent_51%,transparent_100%)] bg-[length:100px_100%]"></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                
                {/* Left Column: Video & Description */}
                <div className="lg:col-span-5 space-y-4">
                  <div className="flex items-center justify-between border-b border-border pb-2 mb-4">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                      <Play className="w-4 h-4" /> Video Walkthrough
                    </h4>
                    <span className="text-xs font-mono text-muted-foreground/50">VID_SRC_YT</span>
                  </div>
                  
                  <div className="rounded-sm overflow-hidden border border-border bg-card shadow-lg">
                    <AspectRatio ratio={16 / 9}>
                      <iframe src={rig.videoUrl} title={`${rig.name} Walkthrough`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                    </AspectRatio>
                  </div>
                  
                  <div className="p-4 bg-card/30 border border-border/50 rounded-sm">
                    <p className="text-sm text-muted-foreground italic">
                      "{rig.description}"
                    </p>
                  </div>
                </div>

                {/* Right Column: Specs Table */}
                <div className="lg:col-span-7">
                  <div className="flex items-center justify-between border-b border-border pb-2 mb-4">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                      <Settings className="w-4 h-4" /> Component Manifest
                    </h4>
                    <span className="text-xs font-mono text-muted-foreground/50">REV_1.4</span>
                  </div>

                  <div className="border border-border rounded-sm overflow-hidden bg-card/20">
                    <Table>
                      <TableCaption className="text-xs font-mono text-muted-foreground/60 pb-4">
                        * Specifications subject to change based on mission requirements.
                      </TableCaption>
                      <TableHeader className="bg-muted/20">
                        <TableRow className="hover:bg-transparent border-border">
                          <TableHead className="w-[30%] text-primary font-bold uppercase text-xs tracking-wider">Component</TableHead>
                          <TableHead className="w-[25%] text-muted-foreground font-bold uppercase text-xs tracking-wider">Brand</TableHead>
                          <TableHead className="w-[30%] text-muted-foreground font-bold uppercase text-xs tracking-wider">Modification</TableHead>
                          <TableHead className="text-right text-muted-foreground font-bold uppercase text-xs tracking-wider">Review</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rig.specs.map((spec, i) => <TableRow key={i} className="border-border/50 hover:bg-muted/10 transition-colors">
                            <TableCell className="font-medium text-foreground text-sm">{spec.component}</TableCell>
                            <TableCell className="text-muted-foreground text-sm">{spec.brand}</TableCell>
                            <TableCell className="text-muted-foreground text-sm font-mono text-xs">{spec.type}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-primary/20 hover:text-primary" asChild>
                                <Link to={spec.link} aria-label={`Read review for ${spec.brand} ${spec.component}`}>
                                  <ExternalLink className="w-3 h-3" />
                                </Link>
                              </Button>
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </div>
                  
                  <div className="mt-6 flex justify-end">
                    <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary/10 hover:text-primary hover:border-primary font-mono text-xs uppercase tracking-wider" asChild>
                      <Link to="/OutAndBackPage">
                        View Full Gear Guide <ChevronRight className="w-3 h-3 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Separator Between Rigs */}
              {index < rigs.length - 1 && <div className="flex items-center gap-4 py-16 md:py-24 opacity-40 select-none">
                  <Separator className="flex-1 bg-border" />
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-muted-foreground uppercase whitespace-nowrap">
                    <Activity className="w-3 h-3" />
                    <span>Sector Break</span>
                  </div>
                  <Separator className="flex-1 bg-border" />
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
}